import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import apiService from '../services/api';
import { markChapterAsRead } from '../services/readingService';
import { getUniqueChapterNumber } from '../services/bibleService';
import { getFromStorage, STORAGE_KEYS } from '../utils/storageUtils';
import { getReaderReadingHistory } from '../services/readerService';

function BibleChapter() {
  console.log('BibleChapter component rendering');
  
  const [passage, setPassage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedReader, setSelectedReader] = useState('');
  const [selectedReaderName, setSelectedReaderName] = useState('');
  const [notes, setNotes] = useState('');
  const [markingAsRead, setMarkingAsRead] = useState(false);
  const [existingNotes, setExistingNotes] = useState(null);
  const [checkingExistingNotes, setCheckingExistingNotes] = useState(false);
  const { bookId: book, chapterId: chapter } = useParams();
  
  console.log('URL parameters:', { book, chapter });

  // Fetch passage data
  useEffect(() => {
    const fetchPassage = async () => {
      if (!book || !chapter) return;
      
      try {
        setLoading(true);
        setError(null);
        console.log(`Fetching Bible passage for ${book} ${chapter}...`);
        const response = await apiService.get(`/bible/passage/${book}/${chapter}`);
        console.log('Passage response received:', response);
        setPassage(response);
        setLoading(false);
        
        // Scroll to top when loading a new chapter
        window.scrollTo(0, 0);
      } catch (err) {
        console.error('Failed to fetch Bible passage:', err);
        console.error('Error details:', {
          message: err.message,
          response: err.response ? {
            status: err.response.status,
            statusText: err.response.statusText,
            data: err.response.data
          } : 'No response data',
          request: err.request ? 'Request object exists' : 'No request object'
        });
        setError(`Failed to load Bible passage: ${err.message}. Please try again later.`);
        setLoading(false);
      }
    };
    
    fetchPassage();
  }, [book, chapter]);

  // Initialize selected reader from localStorage
  useEffect(() => {
    const savedReader = getFromStorage(STORAGE_KEYS.SELECTED_READER);
    if (savedReader) {
      setSelectedReader(savedReader);
    }
  }, []);

  // Listen for reader changes from the ReaderSelector component
  useEffect(() => {
    const handleReaderChange = (event) => {
      const { readerId, readerName } = event.detail;
      console.log('BibleChapter - Reader changed event received:', readerId, readerName);
      setSelectedReader(readerId);
      setSelectedReaderName(readerName);
    };
    
    window.addEventListener('readerChanged', handleReaderChange);
    
    return () => {
      window.removeEventListener('readerChanged', handleReaderChange);
    };
  }, []);

  // Check if the chapter has already been read and has notes
  useEffect(() => {
    const checkExistingNotes = async () => {
      if (!selectedReader || !book || !chapter) return;
      
      try {
        setCheckingExistingNotes(true);
        const response = await getReaderReadingHistory(selectedReader);
        
        if (response && response.chaptersRead) {
          const existingEntry = response.chaptersRead.find(
            entry => entry.book === book && entry.chapter.toString() === chapter.toString()
          );
          
          if (existingEntry && existingEntry.notes) {
            setExistingNotes(existingEntry);
          } else {
            setExistingNotes(null);
          }
        }
        
        setCheckingExistingNotes(false);
      } catch (err) {
        console.error('Failed to check existing notes:', err);
        setCheckingExistingNotes(false);
      }
    };
    
    checkExistingNotes();
  }, [selectedReader, book, chapter]);

  // Handle marking chapter as read
  const handleMarkAsRead = async (e) => {
    e.preventDefault();
    if (!selectedReader) {
      setError('Please select a reader from the header first');
      return;
    }

    try {
      setMarkingAsRead(true);
      setError(null);
      
      // Get the unique chapter number from the API
      console.log(`Getting unique chapter number for ${book} ${chapter}`);
      const chapterResponse = await getUniqueChapterNumber(book, chapter);
      console.log('Chapter response:', JSON.stringify(chapterResponse, null, 2));
      
      // Extract the unique chapter number from the response
      // The response structure might be different depending on your API
      let uniqueChapterNumber;
      
      if (chapterResponse.chapter_id) {
        uniqueChapterNumber = chapterResponse.chapter_id;
      } else if (chapterResponse.id) {
        uniqueChapterNumber = chapterResponse.id;
      } else if (chapterResponse.unique_chapter_number) {
        uniqueChapterNumber = chapterResponse.unique_chapter_number;
      } else if (chapterResponse.data) {
        // Try to find the chapter ID in the data object
        const data = chapterResponse.data;
        uniqueChapterNumber = data.chapter_id || data.id || data.unique_chapter_number;
      }
      
      if (!uniqueChapterNumber) {
        console.error('Could not find unique chapter number in response:', chapterResponse);
        throw new Error('Could not determine unique chapter number');
      }
      
      console.log(`Using unique chapter number: ${uniqueChapterNumber}`);
      
      // Mark the chapter as read
      const readingResponse = await markChapterAsRead({
        readerId: selectedReader,
        chapterId: uniqueChapterNumber,
        notes: notes.trim() || undefined
      });
      
      console.log('Reading response:', readingResponse);
      
      setSuccess(`${book} ${chapter} marked as read!`);
      setNotes('');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to mark chapter as read:', err);
      setError('Failed to mark chapter as read. Please try again.');
    } finally {
      setMarkingAsRead(false);
    }
  };

  // Handle using existing notes
  const handleUseExistingNotes = () => {
    if (existingNotes && existingNotes.notes) {
      setNotes(existingNotes.notes);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading passage...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  if (!passage) {
    return (
      <div className="alert alert-warning" role="alert">
        No passage found. Please select a valid book and chapter.
      </div>
    );
  }

  return (
    <div className="bible-chapter-container">
      <div className="chapter-header">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h1 className="chapter-title">{passage.book} {passage.chapter}</h1>
          <Link to={`/bible/${passage.book}`} className="btn btn-outline-secondary btn-sm">
            <i className="bi bi-arrow-left"></i> Back to {passage.book}
          </Link>
        </div>
        
        <div className="chapter-navigation">
          {parseInt(chapter) > 1 && (
            <Link 
              to={`/bible/${book}/${parseInt(chapter) - 1}`} 
              className="btn btn-outline-primary btn-sm me-2"
            >
              <i className="bi bi-chevron-left"></i> Previous Chapter
            </Link>
          )}
          
          <Link to="/bible" className="btn btn-outline-secondary btn-sm me-2">
            All Books
          </Link>
          
          <Link 
            to={`/bible/${book}/${parseInt(chapter) + 1}`} 
            className="btn btn-outline-primary btn-sm"
          >
            Next Chapter <i className="bi bi-chevron-right"></i>
          </Link>
        </div>
        
        {/* Reading Reports Link */}
        {selectedReader && (
          <div className="reading-reports-link mt-3">
            <Link to={`/reading-reports/${selectedReader}`} className="btn btn-info btn-sm">
              <i className="bi bi-journal-text"></i> View Full Reading Reports
            </Link>
          </div>
        )}
      </div>
      
      {/* Mark as Read Form */}
      <div className="mark-as-read-section card mt-3 mb-4">
        <div className="card-body">
          <h3 className="card-title">Mark as Read</h3>
          
          {success && (
            <div className="alert alert-success" role="alert">
              {success}
            </div>
          )}
          
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          
          {existingNotes && (
            <div className="existing-notes-alert alert alert-info" role="alert">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <strong>You have existing notes for this chapter</strong>
                  <p className="mb-1 text-small">
                    Read on {new Date(existingNotes.read_date || existingNotes.readDate).toLocaleDateString()}
                  </p>
                </div>
                <button 
                  className="btn btn-sm btn-outline-primary" 
                  onClick={handleUseExistingNotes}
                >
                  Use Existing Notes
                </button>
              </div>
              <div className="existing-notes-preview mt-2">
                {existingNotes.notes}
              </div>
            </div>
          )}
          
          <form onSubmit={handleMarkAsRead}>
            <div className="mb-3">
              <label htmlFor="notes" className="form-label">Notes (optional)</label>
              <textarea
                id="notes"
                className="form-control"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this chapter"
                rows="2"
              ></textarea>
            </div>
            
            <button 
              type="submit" 
              className="btn btn-success"
              disabled={markingAsRead || !selectedReader}
            >
              {markingAsRead ? 'Marking...' : 'Mark as Read'}
            </button>
            
            {!selectedReader && (
              <div className="alert alert-info mt-3">
                <small>Please select a reader from the header before marking this chapter as read.</small>
              </div>
            )}
          </form>
        </div>
      </div>
      
      <div className="chapter-content mt-4">
        <div dangerouslySetInnerHTML={{ __html: passage.passageHtml }} />
        
        {passage.audioUrl && (
          <div className="audio-player mt-3">
            <h3>Listen to this Chapter</h3>
            <audio controls className="w-100">
              <source src={passage.audioUrl} type="audio/mpeg" />
              Your browser does not support the audio element.
            </audio>
          </div>
        )}
      </div>
    </div>
  );
}

export default BibleChapter; 